import { useEffect, useRef } from 'react';
import { cohostBell } from '../lib/sounds';

/**
 * Host-only SSE for co-host requests (`GET /api/lives/{id}/host-events`).
 * - reopens the stream on visibilitychange with `visible=1|0` (push suppression);
 * - `snapshot` (connect/reconnect) never rings; only a NEW `cohost_request`
 *   received while visible rings once per request_id;
 * - every event triggers `onChanged()` so the page re-reads `live.guests`.
 * Source of truth stays the server (`lives.guests[]`).
 */
export default function useHostCohostEvents({ liveId, enabled, onChanged, onLiveEnded }) {
  const handledRef = useRef(new Set());
  const esRef = useRef(null);
  const cbRef = useRef({ onChanged, onLiveEnded });
  cbRef.current = { onChanged, onLiveEnded };

  useEffect(() => {
    if (!enabled || !liveId) return undefined;
    const token = localStorage.getItem('ig_token');
    if (!token) return undefined;
    let closed = false;

    const open = () => {
      if (closed) return;
      try { esRef.current?.close(); } catch (_e) { /* noop */ }
      const visible = document.visibilityState === 'visible' ? '1' : '0';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/lives/${encodeURIComponent(liveId)}/host-events?token=${encodeURIComponent(token)}&visible=${visible}`;
      let es;
      try { es = new EventSource(url); } catch (_e) { return; }
      esRef.current = es;
      es.addEventListener('snapshot', (evt) => {
        try {
          const { pending = [] } = JSON.parse(evt.data);
          pending.forEach((p) => handledRef.current.add(p.request_id)); // never ring on snapshot
          cbRef.current.onChanged?.();
        } catch (_e) { /* ignore */ }
      });
      es.addEventListener('cohost_request', (evt) => {
        try {
          const req = JSON.parse(evt.data);
          if (!req.request_id || handledRef.current.has(req.request_id)) return;
          handledRef.current.add(req.request_id);
          if (document.visibilityState === 'visible') cohostBell();
          cbRef.current.onChanged?.();
        } catch (_e) { /* ignore */ }
      });
      es.addEventListener('cohost_request_closed', () => { cbRef.current.onChanged?.(); });
      es.addEventListener('live_ended', () => {
        try { es.close(); } catch (_e) { /* noop */ }
        handledRef.current.clear();
        cbRef.current.onLiveEnded?.();
        cbRef.current.onChanged?.();
      });
      es.onerror = () => { /* EventSource auto-reconnects; snapshot re-syncs */ };
    };

    const onVisibility = () => open();
    open();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try { esRef.current?.close(); } catch (_e) { /* noop */ }
      esRef.current = null;
    };
  }, [liveId, enabled]);
}
